(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.FFCProductFinder = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const DIGITAL_ONLY = 'Digital Only';
  const DIGITAL_WITH_LAMINATED_SHEET = 'Digital + Laminated Sheet';

  function optionValuesMatch(actual, wanted) {
    if (actual === wanted) return true;
    if (wanted === DIGITAL_ONLY) return actual === 'No';
    if (wanted === DIGITAL_WITH_LAMINATED_SHEET) return actual === 'Yes';
    return false;
  }

  const HANDLES = Object.freeze({
    fullBoat: 'full-boat-bundle-ffs-and-3-in-1-sonar-courses-settings-guides',
    threeInOne: 'sonar-mastery-complete-bundle',
    ffs: 'forward-facing-sonar-bundle',
    courseBundle: 'sonar-mastery-video-course-bundle',
    twoD: '2d-sonar-mastery-video-course',
    down: 'down-imaging-mastery-video-course',
    side: 'side-imaging-mastery-video-course',
    ffsCourse: 'forward-facing-sonar-course',
    mega360: 'humminbird-360-mega-360-settings-guide',
  });

  const COURSE_TITLES = Object.freeze({
    twoD: '2D Sonar Mastery Video Course',
    down: 'Down Imaging Mastery Video Course',
    side: 'Side Imaging Mastery Video Course',
    ffs: 'Forward Facing Sonar Video Course',
  });

  function threeInOneOption(value) {
    return value === 'Lowrance TripleShot (Hook Reveal/Hook2)'
      ? 'Lowrance TripleShot non-HD (Hook Reveal/Hook2)'
      : value;
  }

  function ffsBundleOption(value) {
    const map = {
      'Garmin LVS32/LVS34': 'Garmin LVS32/LVS34 Livescope',
      'Lowrance Active Target 1/2': 'Lowrance Active Target 1 or 2',
    };
    return map[value] || value;
  }

  function request(kind, handle, wantedOptions, reason, need, noSettingsGuideMessage) {
    const item = { kind, handle, wantedOptions, reason };
    if (need) Object.defineProperty(item, 'need', { value: need, enumerable: false });
    if (noSettingsGuideMessage) {
      Object.defineProperty(item, 'noSettingsGuideMessage', { value: noSettingsGuideMessage, enumerable: false });
    }
    return item;
  }

  function mega360Request(additional) {
    return request(
      'guide',
      HANDLES.mega360,
      [DIGITAL_ONLY],
      additional
        ? 'MEGA 360 is not contained in the sonar bundles.'
        : 'You selected Humminbird 360/MEGA 360, which has its own settings guide.',
      'mega360-guide'
    );
  }

  function conventionalCourseRequest(modes, conventionalBrand) {
    const selected = Array.from(new Set(Array.isArray(modes) ? modes : [])).filter(function (mode) {
      return mode === '2d' || mode === 'down' || mode === 'side';
    });
    const noSettingsGuideMessage = conventionalBrand === 'airmar'
      ? 'No transducer-specific settings guide is available for Airmar transducers.'
      : (selected.length > 0 && !selected.includes('side')
        ? 'No transducer-specific settings guide is available for a 2D-only or Down Imaging-only transducer.'
        : '');

    if (selected.length >= 2) {
      return request(
        'course',
        HANDLES.courseBundle,
        ['Default Title'],
        'Your exact conventional transducer is not confirmed, so this recommendation contains universal training without a transducer-specific PDF.',
        'conventional-courses',
        noSettingsGuideMessage
      );
    }
    if (selected[0] === 'side') {
      return request(
        'course',
        HANDLES.side,
        ['Default Title'],
        'This universal course teaches Side Imaging without guessing a transducer-specific PDF.',
        'side-course',
        noSettingsGuideMessage
      );
    }
    if (selected[0] === 'down') {
      return request(
        'course',
        HANDLES.down,
        ['Default Title'],
        'This universal course teaches Down Imaging without guessing a transducer-specific PDF.',
        'down-course',
        noSettingsGuideMessage
      );
    }
    if (selected[0] === '2d') {
      return request(
        'course',
        HANDLES.twoD,
        ['Default Title'],
        'This universal course teaches 2D sonar and does not require a transducer-specific PDF.',
        '2d-course',
        noSettingsGuideMessage
      );
    }
    return null;
  }

  function ffsCourseRequest() {
    return request(
      'course',
      HANDLES.ffsCourse,
      ['Default Title'],
      'Your exact forward-facing transducer is not confirmed, so this universal course does not include a transducer-specific PDF.',
      'ffs-course'
    );
  }

  function findExactAvailableVariant(product, wantedOptions) {
    const variants = product && Array.isArray(product.variants) ? product.variants : [];
    return variants.find(function (variant) {
      return Boolean(variant.available) &&
        Array.isArray(variant.options) &&
        variant.options.length === wantedOptions.length &&
        wantedOptions.every(function (value, index) {
          return optionValuesMatch(variant.options[index], value);
        });
    }) || null;
  }

  function optionName(product, index) {
    const option = product && Array.isArray(product.options) ? product.options[index] : null;
    if (typeof option === 'string') return option;
    return option && option.name ? option.name : `Option ${index + 1}`;
  }

  function safeProductImageUrl(rawImage, baseUrl) {
    if (!rawImage) return '';
    const raw = String(rawImage);
    const normalized = raw.startsWith('//') ? `https:${raw}` : raw;
    try {
      const base = new URL(String(baseUrl || 'https://fishfindercoach.com'));
      const parsed = new URL(normalized, base);
      if (parsed.protocol !== 'https:') return '';
      const host = parsed.hostname.toLowerCase();
      const baseIsTrustedHttps = base.protocol === 'https:' && host === base.hostname.toLowerCase();
      const trustedHost = baseIsTrustedHttps ||
        host === 'fishfindercoach.com' || host.endsWith('.fishfindercoach.com') ||
        host === 'cdn.shopify.com' || host.endsWith('.myshopify.com') ||
        host.endsWith('.shopifycdn.net');
      return trustedHost ? parsed.href : '';
    } catch (_) {
      return '';
    }
  }

  function resolvedVariantImageUrl(product, variant, baseUrl) {
    const rawImage = (variant.featured_image && (variant.featured_image.src || variant.featured_image)) ||
      product.featured_image || (Array.isArray(product.images) && product.images[0]) || '';
    return safeProductImageUrl(rawImage, baseUrl);
  }

  async function resolveProduct(productRequest, fetchImpl, baseUrl, cachebuster) {
    const base = String(baseUrl || '').replace(/\/$/, '');
    const fetcher = fetchImpl || (typeof fetch === 'function' ? fetch.bind(globalThis) : null);
    if (!fetcher) throw new Error('No fetch implementation is available.');

    const response = await fetcher(
      `${base}/products/${productRequest.handle}.js?ffc_quiz=${encodeURIComponent(cachebuster || Date.now())}`,
      { cache: 'no-store', credentials: 'same-origin' }
    );
    if (!response || !response.ok) {
      throw new Error(`Product lookup failed for ${productRequest.handle}.`);
    }

    const product = await response.json();
    const variant = findExactAvailableVariant(product, productRequest.wantedOptions);
    if (!variant) {
      throw new Error(`No exact available variant for ${productRequest.handle}.`);
    }

    const imageUrl = resolvedVariantImageUrl(product, variant, baseUrl);
    const allSelectedOptions = productRequest.wantedOptions.map(function (value, index) {
      return { name: optionName(product, index), value };
    });
    const laminatedOptionIndex = allSelectedOptions.findIndex(function (option) {
      return /laminated|quick reference/i.test(option.name);
    });
    const selectedOptions = allSelectedOptions.filter(function (option) {
      return !(String(option.name).trim().toLowerCase() === 'title' &&
        String(option.value).trim().toLowerCase() === 'default title');
    });
    const laminatedChoices = laminatedOptionIndex < 0 ? [] : [DIGITAL_ONLY, DIGITAL_WITH_LAMINATED_SHEET].map(function (value) {
      const wantedOptions = productRequest.wantedOptions.slice();
      wantedOptions[laminatedOptionIndex] = value;
      const choiceVariant = findExactAvailableVariant(product, wantedOptions);
      if (!choiceVariant) return null;
      return {
        value,
        variantId: Number(choiceVariant.id),
        priceCents: Number(choiceVariant.price),
        imageUrl: resolvedVariantImageUrl(product, choiceVariant, baseUrl),
        url: `${base}/products/${productRequest.handle}?variant=${choiceVariant.id}`,
      };
    }).filter(Boolean);

    return {
      productId: Number(product.id),
      variantId: Number(variant.id),
      title: product.title,
      variantTitle: variant.public_title || variant.title || productRequest.wantedOptions.join(' / '),
      priceCents: Number(variant.price),
      imageUrl,
      url: `${base}/products/${productRequest.handle}?variant=${variant.id}`,
      selectedOptions,
      hasLaminatedOption: laminatedOptionIndex >= 0,
      laminatedChoices,
      request: productRequest,
    };
  }

  async function addVariantToCart(variantId, fetchImpl, baseUrl, properties) {
    const base = String(baseUrl || '').replace(/\/$/, '');
    const fetcher = fetchImpl || (typeof fetch === 'function' ? fetch.bind(globalThis) : null);
    const id = Number(variantId);
    if (!Number.isFinite(id) || id <= 0) throw new Error('A valid variant ID is required.');
    if (!fetcher) throw new Error('No fetch implementation is available.');

    const body = new URLSearchParams({ id: String(id), quantity: '1' });
    Object.entries(properties || {}).forEach(function (entry) {
      const key = String(entry[0]);
      const value = entry[1];
      if (!/^_ffc_[a-z0-9_]+$/.test(key) || value === null || value === undefined) return;
      body.append(`properties[${key}]`, String(value));
    });

    const response = await fetcher(`${base}/cart/add.js`, {
      method: 'POST',
      body: body.toString(),
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
    const payload = response && typeof response.json === 'function' ? await response.json() : null;
    if (!response || !response.ok || (payload && payload.status === 422)) {
      const message = payload && (payload.description || payload.message);
      throw new Error(message || 'Shopify could not add this exact variant to the cart.');
    }
    return payload;
  }

  function describeIncluded(product) {
    const productRequest = product && product.request ? product.request : product;
    const handle = productRequest && productRequest.handle;
    const options = productRequest && Array.isArray(productRequest.wantedOptions)
      ? productRequest.wantedOptions
      : [];
    const included = { settingsGuides: [], videoCourses: [], notes: [] };

    if (handle === HANDLES.fullBoat) {
      included.settingsGuides = [
        `${options[0]} PDF Settings Guide`,
        `${options[1]} PDF Settings Guide`,
      ];
      included.videoCourses = [COURSE_TITLES.twoD, COURSE_TITLES.down, COURSE_TITLES.side, COURSE_TITLES.ffs];
    } else if (handle === HANDLES.threeInOne) {
      included.settingsGuides = [`${options[0]} PDF Settings Guide`];
      included.videoCourses = [COURSE_TITLES.twoD, COURSE_TITLES.down, COURSE_TITLES.side];
    } else if (handle === HANDLES.ffs) {
      included.settingsGuides = [`${options[0]} PDF Settings Guide`];
      included.videoCourses = [COURSE_TITLES.ffs];
    } else if (handle === HANDLES.courseBundle) {
      included.videoCourses = [COURSE_TITLES.twoD, COURSE_TITLES.down, COURSE_TITLES.side];
      included.notes.push('Courses are universal across fishfinder brands. No transducer-specific PDF is included.');
    } else if (handle === HANDLES.twoD) {
      included.videoCourses = [COURSE_TITLES.twoD];
      included.notes.push('No transducer-specific PDF is included.');
    } else if (handle === HANDLES.down) {
      included.videoCourses = [COURSE_TITLES.down];
      included.notes.push('No transducer-specific PDF is included.');
    } else if (handle === HANDLES.side) {
      included.videoCourses = [COURSE_TITLES.side];
      included.notes.push('No transducer-specific PDF is included.');
    } else if (handle === HANDLES.ffsCourse) {
      included.videoCourses = [COURSE_TITLES.ffs];
      included.notes.push('No transducer-specific PDF is included.');
    } else if (handle === HANDLES.mega360) {
      included.settingsGuides = ['Humminbird 360/MEGA 360 PDF Settings Guide'];
      included.notes.push('This standalone guide does not include a dedicated video course.');
    }
    return included;
  }

  function buildRecommendation(selection) {
    const input = selection || {};
    const conventional = input.conventional || '';
    const ffs = input.ffs || '';
    const mega360 = Boolean(input.mega360);
    const conventionalKnown = Boolean(conventional && conventional !== 'UNKNOWN' && conventional !== '2D_ONLY');
    const ffsKnown = Boolean(ffs && ffs !== 'UNKNOWN');
    const conventionalUnknown = conventional === 'UNKNOWN';
    const ffsUnknown = ffs === 'UNKNOWN' || (input.hasFfs === true && !ffs);
    const identificationScopes = [];
    if (conventionalUnknown) identificationScopes.push('conventional');
    if (ffsUnknown) identificationScopes.push('ffs');

    const twoDPrimary = request(
      'course',
      HANDLES.twoD,
      ['Default Title'],
      'You selected 2D sonar without a supported 3-in-1 transducer.',
      '2d-course',
      'No transducer-specific settings guide is available for a 2D-only transducer.'
    );
    const twoDAdditional = request(
      'course',
      HANDLES.twoD,
      ['Default Title'],
      '2D-only training is not contained in the forward-facing sonar bundle.',
      '2d-course',
      'No transducer-specific settings guide is available for a 2D-only transducer.'
    );
    const conventionalFallback = conventional === '2D_ONLY'
      ? (ffsKnown ? twoDAdditional : twoDPrimary)
      : (conventionalUnknown ? conventionalCourseRequest(input.sonarModes, input.conventionalBrand) : null);
    const forwardFallback = ffsUnknown ? ffsCourseRequest() : null;
    const candidates = [];
    const omittedNeeds = [];

    function pushUnique(item) {
      if (!item) return;
      if (candidates.some(function (existing) { return existing.need === item.need; })) return;
      candidates.push(item);
    }

    let exactPrimary = null;
    if (conventionalKnown && ffsKnown) {
      exactPrimary = request(
        'bundle',
        HANDLES.fullBoat,
        [conventional, ffs, DIGITAL_ONLY],
        'You selected both conventional/side imaging and forward-facing sonar.',
        'full-boat'
      );
    } else if (conventionalKnown) {
      exactPrimary = request(
        'bundle',
        HANDLES.threeInOne,
        [threeInOneOption(conventional), DIGITAL_ONLY],
        'You selected a supported conventional/side-imaging transducer.',
        'three-in-one'
      );
    } else if (ffsKnown) {
      exactPrimary = request(
        'bundle',
        HANDLES.ffs,
        [ffsBundleOption(ffs), DIGITAL_ONLY],
        'You selected a supported forward-facing sonar transducer.',
        'ffs-bundle'
      );
    }

    if (exactPrimary) pushUnique(exactPrimary);
    pushUnique(conventionalFallback);
    pushUnique(forwardFallback);
    if (mega360) pushUnique(mega360Request(candidates.length > 0));

    const recommendations = candidates.slice(0, 2);
    candidates.slice(2).forEach(function (item) {
      if (item.need && !omittedNeeds.includes(item.need)) omittedNeeds.push(item.need);
    });

    return {
      primary: recommendations[0] || null,
      secondary: recommendations.slice(1),
      recommendations,
      needsIdentification: identificationScopes.length > 0 || recommendations.length === 0,
      identificationScopes,
      omittedNeeds,
    };
  }

  return {
    HANDLES,
    buildRecommendation,
    findExactAvailableVariant,
    resolveProduct,
    addVariantToCart,
    describeIncluded,
  };
});
